import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Building2,
  UserCheck,
  Users,
  Calendar,
  TrendingUp,
  TrendingDown,
  Activity,
  Bed,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { analyticsAPI } from '../services/api';

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 0.5rem;
`;

const StatChange = styled.div<{ isPositive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: ${props => props.isPositive ? '#10b981' : '#ef4444'};
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const ChartTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const RecentActivity = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
`;

const ActivityIcon = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 0.25rem;
`;

const ActivityTime = styled.div`
  font-size: 0.875rem;
  color: #64748b;
`;

interface MonthlyAppointment {
  month: string;
  appointments: number;
}

interface BedOccupancy {
  name: string;
  occupied: number;
  total: number;
}

interface HospitalType {
  name: string;
  count: number;
}

interface ChartData {
  monthlyAppointments: MonthlyAppointment[];
  bedOccupancy: BedOccupancy[];
  hospitalTypes: HospitalType[];
}

interface Stats {
  totalHospitals: number;
  totalDoctors: number;
  totalPatients: number;
  totalAppointments: number;
  hospitalChange: number;
  doctorChange: number;
  patientChange: number;
  appointmentChange: number;
}

interface Activity {
  id: string;
  type: string;
  title: string;
  time: string;
  color?: string;
  action?: string;
  user?: string;
  icon?: React.ReactNode;
}

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'];

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats>({
    totalHospitals: 0,
    totalDoctors: 0,
    totalPatients: 0,
    totalAppointments: 0,
    hospitalChange: 0,
    doctorChange: 0,
    patientChange: 0,
    appointmentChange: 0,
  });

  const [chartData, setChartData] = useState<ChartData>({
    monthlyAppointments: [],
    bedOccupancy: [],
    hospitalTypes: [],
  });

  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await analyticsAPI.getDashboardStats();
      if (response.success) {
        setStats(response.data.stats);
        setChartData(response.data.charts);
        setRecentActivities(response.data.recentActivities);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Set mock data for demo
      setStats({
        totalHospitals: 45,
        totalDoctors: 324,
        totalPatients: 1250,
        totalAppointments: 89,
        hospitalChange: 12,
        doctorChange: 8,
        patientChange: 15,
        appointmentChange: -5,
      });
      
      setChartData({
        monthlyAppointments: [
          { month: 'Jan', appointments: 120 },
          { month: 'Feb', appointments: 150 },
          { month: 'Mar', appointments: 180 },
          { month: 'Apr', appointments: 160 },
          { month: 'May', appointments: 200 },
          { month: 'Jun', appointments: 220 },
        ],
        bedOccupancy: [
          { name: 'General', occupied: 85, total: 100 },
          { name: 'ICU', occupied: 12, total: 15 },
          { name: 'Emergency', occupied: 8, total: 10 },
        ],
        hospitalTypes: [
          { name: 'General', count: 25 },
          { name: 'Specialty', count: 12 },
          { name: 'Emergency', count: 8 },
        ],
      });
      
      setRecentActivities([
        {
          id: '1',
          type: 'hospital',
          title: 'New hospital added: City General Hospital',
          time: '2 hours ago',
          color: '#667eea',
        },
        {
          id: '2',
          type: 'doctor',
          title: 'Dr. Sarah Johnson joined Cardiology dept',
          time: '4 hours ago',
          color: '#10b981',
        },
        {
          id: '3',
          type: 'appointment',
          title: '15 new appointments scheduled today',
          time: '6 hours ago',
          color: '#f59e0b',
        },
      ]);
    }
  };

  const statCards = [
    {
      icon: Building2,
      label: 'Total Hospitals',
      value: stats.totalHospitals,
      change: stats.hospitalChange,
      color: '#667eea',
    },
    {
      icon: UserCheck,
      label: 'Total Doctors',
      value: stats.totalDoctors,
      change: stats.doctorChange,
      color: '#10b981',
    },
    {
      icon: Users,
      label: 'Total Patients',
      value: stats.totalPatients,
      change: stats.patientChange,
      color: '#f59e0b',
    },
    {
      icon: Calendar,
      label: 'Today\'s Appointments',
      value: stats.totalAppointments,
      change: stats.appointmentChange,
      color: '#ef4444',
    },
  ];

  return (
    <DashboardContainer>
      <PageHeader>
        <PageTitle>Dashboard Overview</PageTitle>
      </PageHeader>

      <StatsGrid>
        {statCards.map((card, index) => (
          <StatCard key={index}>
            <StatHeader>
              <StatIcon color={card.color}>
                <card.icon size={24} />
              </StatIcon>
            </StatHeader>
            <StatValue>{card.value.toLocaleString()}</StatValue>
            <StatLabel>{card.label}</StatLabel>
            <StatChange isPositive={card.change >= 0}>
              {card.change >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              {Math.abs(card.change)}% from last month
            </StatChange>
          </StatCard>
        ))}
      </StatsGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>Monthly Appointments</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData.monthlyAppointments}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="appointments" fill="#667eea" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Hospital Distribution</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData.hospitalTypes}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {chartData.hospitalTypes.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </ChartsGrid>

      <RecentActivity>
        <ChartTitle>Recent Activity</ChartTitle>
        <ActivityList>
          {recentActivities.map((activity: any) => (
            <ActivityItem key={activity.id}>
              <ActivityIcon color={activity.color}>
                <Activity size={20} />
              </ActivityIcon>
              <ActivityContent>
                <ActivityTitle>{activity.title}</ActivityTitle>
                <ActivityTime>{activity.time}</ActivityTime>
              </ActivityContent>
            </ActivityItem>
          ))}
        </ActivityList>
      </RecentActivity>
    </DashboardContainer>
  );
};

export default Dashboard;
